/**
 * Google Maps configuration and utilities
 */

// Snazzy Maps styling configuration
// Type is any[] to avoid ReferenceError when google is not yet loaded
export const SNAZZY_MAPS_STYLE: any[] = [
  {
    featureType: "all",
    elementType: "labels.text",
    stylers: [{ color: "#878787" }],
  },
  {
    featureType: "all",
    elementType: "labels.text.stroke",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape",
    elementType: "all",
    stylers: [{ color: "#f9f5ed" }],
  },
  {
    featureType: "road.highway",
    elementType: "all",
    stylers: [{ color: "#f5f5f5" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#c9c9c9" }],
  },
  {
    featureType: "water",
    elementType: "all",
    stylers: [{ color: "#aee0f4" }],
  },
];

/**
 * Get Google Maps API key from environment
 * Logs warnings in both development and production to help debug Vercel issues
 * Verifies the key is actually set and not empty
 */
export function getGoogleMapsApiKey(): string {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (import.meta.env.DEV) {
    if (!key || key.trim() === "") {
      console.warn("⚠️ VITE_GOOGLE_MAPS_API_KEY not configured or empty");
    }
  }

  return (window as any).VINTMAP_CONFIG?.googleMapsApiKey || key?.trim() || "";
}

/**
 * Get default map options with optional Map ID
 * Type is any to avoid ReferenceError when google is not yet loaded
 * 
 * @param mapId - Optional Map ID for Advanced Markers support
 */
export function getDefaultMapOptions(mapId?: string): any {
  return {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    // Map ID is required for Advanced Markers
    ...(mapId && { mapId }),
  };
}

/**
 * Default map options (legacy, without Map ID)
 * @deprecated Use getDefaultMapOptions() instead
 */
export const DEFAULT_MAP_OPTIONS: any = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
};

/**
 * Get default center for Netherlands
 * Type is any to avoid ReferenceError when google is not yet loaded
 */
export const DEFAULT_CENTER: any = {
  lat: 52.1326,
  lng: 5.2913,
};

/**
 * Get default zoom level
 */
export const DEFAULT_ZOOM = 7;

/**
 * Google Maps libraries - defined as constant to prevent reload warnings
 * Must be defined outside components
 */
export const GOOGLE_MAPS_LIBRARIES: ("places" | "marker" | "geometry" | "drawing" | "visualization")[] = ["places", "marker", "geometry"];

/**
 * Get Google Maps Map ID from environment
 * Required for Advanced Markers (AdvancedMarkerElement)
 * 
 * To create a Map ID:
 * 1. Go to Google Cloud Console → Maps → Map Management → Map IDs
 * 2. Click "Create Map ID"
 * 3. Choose "JavaScript" as type
 * 4. Give it a name like "vintmap-production"
 * 5. Add the Map ID to your environment variables as VITE_GOOGLE_MAPS_MAP_ID
 */
export function getGoogleMapsMapId(): string | undefined {
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

  if (import.meta.env.DEV) {
    if (!mapId || mapId.trim() === "") {
      console.warn("⚠️ VITE_GOOGLE_MAPS_MAP_ID not configured");
    }
  }

  return (window as any).VINTMAP_CONFIG?.googleMapsMapId || mapId?.trim() || undefined;
}

/**
 * Google Maps Navigation Helper Functions
 */

import type { Shop } from "@shared/schema";

/**
 * Generate Google Maps navigation URL for a single destination
 */
export function getGoogleMapsNavigationUrl(lat: number, lng: number, label?: string): string {
  const destination = `${lat},${lng}`;
  const params = new URLSearchParams({
    api: '1',
    destination,
  });

  if (label) {
    params.set('destination_place_id', label);
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Generate Google Maps navigation URL for a shop
 */
export function getShopNavigationUrl(shop: Shop): string {
  if (!shop.lat || !shop.lng) {
    // Fallback to address search if no coordinates
    const shopName = typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || 'Winkel';
    const cityName = typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || '';
    const address = `${shopName}, ${shop.address}, ${cityName}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  const shopName = typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || 'Winkel';
  return getGoogleMapsNavigationUrl(shop.lat, shop.lng, shopName);
}

/**
 * Generate Google Maps URL with multiple waypoints for a route
 */
export function getRouteNavigationUrl(shops: Shop[]): string {
  if (shops.length === 0) return '';

  // Filter shops with valid coordinates
  const validShops = shops.filter(s => s.lat && s.lng);

  if (validShops.length === 0) return '';

  if (validShops.length === 1) {
    return getShopNavigationUrl(validShops[0]);
  }

  // First shop is destination, rest are waypoints
  const destination = `${validShops[0].lat},${validShops[0].lng}`;
  const waypoints = validShops.slice(1).map(s => `${s.lat},${s.lng}`).join('|');

  const params = new URLSearchParams({
    api: '1',
    destination,
    waypoints,
    travelmode: 'driving',
  });

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Open Google Maps in new window/tab or native app
 */
export function openGoogleMaps(url: string): void {
  // On mobile, try to use the native app
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // Try to open in Google Maps app
    window.location.href = url;
  } else {
    // On desktop, open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

import { getGeoapifyStaticMapUrl } from "./osm";

export function getStaticMapUrl(options: {
  center?: { lat: number, lng: number },
  zoom?: number,
  size?: string,
  markers?: Array<{ lat: number, lng: number, label?: string, color?: string }>,
  path?: string,
}): string {
  let width = 600;
  let height = 600;
  if (options.size && options.size.includes('x')) {
    const [w, h] = options.size.split('x').map(Number);
    if (!isNaN(w)) width = w;
    if (!isNaN(h)) height = h;
  }

  // Use Geoapify for all static maps (high reliability, consolidated service)
  const markers = options.markers || (options.center ? [{ ...options.center }] : []);
  if (markers.length > 0) {
    return getGeoapifyStaticMapUrl({ markers, width, height, polyline: options.path });
  }

  return "";
}
